import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "button" ]
  static values = {
    loadingText: { type: String, default: "Публикуем…" },
    pendingText: { type: String, default: "Дожидаемся загрузки…" }
  }

  connect() {
    this.submitting = false
    this.pending = false
    this.submitRequested = false
    this.activeUploadTokens = new Set()
    this.activeButton = null
    this.originalHtml = null
    this.isIconOnly = false
    this.form = this.element.closest("form")

    if (this.form) {
      this.startedHandler = (event) => this.uploadStarted(event)
      this.finishedHandler = (event) => this.uploadFinished(event)
      this.form.addEventListener("upload:started", this.startedHandler)
      this.form.addEventListener("upload:finished", this.finishedHandler)
    }
  }

  disconnect() {
    if (this.form) {
      if (this.startedHandler) this.form.removeEventListener("upload:started", this.startedHandler)
      if (this.finishedHandler) this.form.removeEventListener("upload:finished", this.finishedHandler)
    }
  }

  uploadStarted(event) {
    if (event.detail?.token) {
      this.activeUploadTokens.add(event.detail.token)
    }
  }

  uploadFinished(event) {
    if (event.detail?.token) {
      this.activeUploadTokens.delete(event.detail.token)
    }

    if (this.activeUploadTokens.size === 0 && this.submitRequested && this.form) {
      this.submitRequested = false
      this.pending = false
      this.form.requestSubmit()
    }
  }

  submit(event) {
    if (event.defaultPrevented) return
    if (this.submitting) {
      event.preventDefault()
      return
    }

    const button = event.submitter || (this.hasButtonTarget ? this.buttonTarget : null)
    if (!button || button.disabled) return

    if (this.activeUploadTokens.size > 0) {
      event.preventDefault()
      if (!this.submitRequested) {
        this.submitRequested = true
        this.pending = true
        this.captureButton(button)
        this.setButtonContent(button, this.pendingHtml())
      }
      return
    }

    this.submitting = true
    this.submitRequested = false
    this.pending = false
    this.captureButton(button)

    button.disabled = true
    button.setAttribute("aria-busy", "true")
    this.setButtonContent(button, this.loaderHtml())
  }

  captureButton(button) {
    this.activeButton = button
    this.originalHtml = button.dataset.originalHtml || this.buttonContent(button)
    button.dataset.originalHtml = this.originalHtml
    this.isIconOnly = this.buttonText(button).trim().length === 0
  }

  reset() {
    if (this.activeButton) {
      this.activeButton.disabled = false
      this.activeButton.removeAttribute("aria-busy")
      this.setButtonContent(this.activeButton, this.originalHtml)
      this.activeButton = null
      this.originalHtml = null
    }
    this.isIconOnly = false
    this.submitting = false
    this.submitRequested = false
    this.pending = false
  }

  pendingHtml() {
    const spinner = `
      <svg class="animate-spin h-4 w-4 text-current inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    `

    if (this.isIconOnly) {
      return { html: `${spinner}<span class="sr-only">${this.pendingTextValue}</span>`, text: this.pendingTextValue }
    }

    return { html: `${spinner}<span class="ml-2">${this.pendingTextValue}</span>`, text: this.pendingTextValue }
  }

  loaderHtml() {
    const spinner = `
      <svg class="animate-spin h-4 w-4 text-current inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    `

    if (this.isIconOnly) {
      return { html: `${spinner}<span class="sr-only">${this.loadingTextValue}</span>`, text: this.loadingTextValue }
    }

    return { html: `${spinner}<span class="ml-2">${this.loadingTextValue}</span>`, text: this.loadingTextValue }
  }

  isInputSubmit(button) {
    return button.tagName === "INPUT" && button.type === "submit"
  }

  buttonText(button) {
    return this.isInputSubmit(button) ? (button.value || "") : button.textContent
  }

  buttonContent(button) {
    return this.isInputSubmit(button) ? (button.value || "") : button.innerHTML
  }

  setButtonContent(button, content) {
    if (this.isInputSubmit(button)) {
      button.value = typeof content === "string" ? content : content.text
    } else {
      button.innerHTML = typeof content === "string" ? content : content.html
    }
  }
}
